import React from "react";

const IndependenceOfReporting = () => {
  const independencePrinciples = [
    {
      id: 1,
      title: "Primary Responsibility to Audience",
      icon: "🎯",
      description:
        "First and foremost, Citizen Reporters and Professional Journalists have a responsibility to the recipients of their work.",
      details: [
        "Audience interests above all other considerations",
        "Truthful and accurate information delivery",
        "Service to public interest and democratic values",
        "Accountability to news consumers",
      ],
      ethicalBasis: "Core Principle of Journalistic Integrity",
    },
    {
      id: 2,
      title: "Independence from Commercial Influences",
      icon: "🚫",
      description:
        "This work must be independent of advertisements and PR campaigns.",
      details: [
        "No influence from advertising revenue on editorial content",
        "Separation of business and editorial departments",
        "Resistance to commercial pressure in reporting",
        "Clear distinction between news and promotional content",
      ],
      ethicalBasis: "Advertising-Editorial Separation Principle",
    },
    {
      id: 3,
      title: "Strict Separation of PR Contracts",
      icon: "📋",
      description:
        "A Citizen Reporters and Professional Journalists must strictly separate PR contracts from journalistic reporting.",
      details: [
        "No dual roles in PR and journalism",
        "Clear contractual boundaries",
        "Disclosure of any PR affiliations",
        "Avoidance of conflict of interest situations",
      ],
      ethicalBasis: "Conflict of Interest Prevention",
    },
  ];

  const separationGuidelines = [
    {
      category: "Content Separation",
      items: [
        "Clear visual distinction between news and ads",
        "No 'advertorial' content disguised as news",
        "Separate layout and design for commercial content",
        "Prominent labeling of sponsored content",
      ],
      icon: "📰",
    },
    {
      category: "Structural Independence",
      items: [
        "Separate editorial and advertising departments",
        "Independent editorial decision-making process",
        "No advertiser influence on news selection",
        "Protected newsroom from commercial pressures",
      ],
      icon: "🏢",
    },
    {
      category: "Personal Conduct",
      items: [
        "No acceptance of gifts from PR agencies",
        "Disclosure of any external relationships",
        "Recusal from stories with personal connections",
        "Regular conflict of interest declarations",
      ],
      icon: "👤",
    },
  ];

  const conflictPrevention = [
    {
      scenario: "PR Contract Offers",
      guidelines: [
        "Immediate disclosure to editorial leadership",
        "Recusal from related reporting assignments",
        "Clear separation of professional roles",
        "Contract review by ethics committee",
      ],
      riskLevel: "High",
    },
    {
      scenario: "Advertising Partnerships",
      guidelines: [
        "No influence on editorial coverage",
        "Separate team handling partnerships",
        "Clear disclosure to audience",
        "Regular ethics compliance audits",
      ],
      riskLevel: "Medium",
    },
    {
      scenario: "Sponsored Content",
      guidelines: [
        "Prominent 'Sponsored' labeling",
        "Distinct visual design from editorial content",
        "No journalist involvement in creation",
        "Separate publishing channels when possible",
      ],
      riskLevel: "Medium",
    },
  ];

  const complianceMeasures = [
    {
      measure: "Regular Audits",
      description:
        "Quarterly reviews of editorial independence and separation compliance",
      frequency: "Quarterly",
    },
    {
      measure: "Ethics Training",
      description:
        "Mandatory training on identifying and avoiding conflicts of interest",
      frequency: "Annual",
    },
    {
      measure: "Disclosure Requirements",
      description:
        "Systematic disclosure of external relationships and potential conflicts",
      frequency: "Ongoing",
    },
    {
      measure: "Whistleblower Protection",
      description: "Protected channels for reporting independence violations",
      frequency: "24/7 Availability",
    },
  ];

  return (
    <div className="min-h-screen bg-gray-50">
      {/* Hero Section */}
      <section className="bg-gradient-to-r from-green-900 to-green-700 text-white py-20">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center">
            <h1 className="text-5xl md:text-6xl font-bold mb-6">
              Independence of Reporting
            </h1>
            <p className="text-xl md:text-2xl text-green-100 max-w-4xl mx-auto">
              Ensuring Editorial Independence and Separation from Commercial
              Influences
            </p>
          </div>
        </div>
      </section>

      {/* Introduction Section */}
      <section className="py-16 bg-white">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-12">
            <h2 className="text-3xl font-bold text-gray-900 mb-4">
              The Foundation of Credible Journalism
            </h2>
            <div className="w-24 h-1 bg-green-600 mx-auto rounded-full"></div>
          </div>

          <div className="prose prose-lg max-w-none text-gray-700 text-center">
            <p className="text-lg leading-relaxed mb-6">
              The independence of reporting is the cornerstone of trustworthy
              journalism. NAI mandates strict separation between editorial
              content and commercial interests to maintain public trust and
              journalistic integrity.
            </p>
          </div>
        </div>
      </section>

      {/* Core Principles */}
      <section className="py-20 bg-green-50">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-16">
            <h2 className="text-4xl font-bold text-gray-900 mb-4">
              Core Principles of Independent Reporting
            </h2>
            <p className="text-xl text-gray-600 max-w-3xl mx-auto">
              Fundamental guidelines ensuring journalistic independence and
              integrity
            </p>
            <div className="w-24 h-1 bg-green-600 mx-auto mt-6 rounded-full"></div>
          </div>

          <div className="grid grid-cols-1 lg:grid-cols-3 gap-8">
            {independencePrinciples.map((principle) => (
              <div
                key={principle.id}
                className="bg-white rounded-2xl shadow-lg hover:shadow-xl transition-all duration-500 overflow-hidden border border-gray-100 group"
              >
                {/* Header */}
                <div className="bg-gradient-to-r from-green-600 to-green-700 p-6">
                  <div className="flex items-center justify-center mb-4">
                    <div className="text-4xl">{principle.icon}</div>
                  </div>
                  <h3 className="text-xl font-bold text-white text-center">
                    {principle.title}
                  </h3>
                </div>

                {/* Content */}
                <div className="p-6">
                  <p className="text-gray-700 text-lg leading-relaxed mb-6">
                    {principle.description}
                  </p>

                  <div className="space-y-4">
                    <div>
                      <h4 className="font-semibold text-gray-900 text-sm uppercase tracking-wide mb-3">
                        Key Requirements:
                      </h4>
                      <ul className="space-y-2">
                        {principle.details.map((detail, index) => (
                          <li key={index} className="flex items-start">
                            <svg
                              className="w-5 h-5 text-green-500 mr-3 mt-0.5 flex-shrink-0"
                              fill="currentColor"
                              viewBox="0 0 20 20"
                            >
                              <path
                                fillRule="evenodd"
                                d="M16.707 5.293a1 1 0 010 1.414l-8 8a1 1 0 01-1.414 0l-4-4a1 1 0 011.414-1.414L8 12.586l7.293-7.293a1 1 0 011.414 0z"
                                clipRule="evenodd"
                              />
                            </svg>
                            <span className="text-gray-600 text-sm">
                              {detail}
                            </span>
                          </li>
                        ))}
                      </ul>
                    </div>

                    <div className="border-t border-gray-200 pt-4">
                      <p className="text-xs text-green-600 font-semibold uppercase tracking-wide">
                        Ethical Basis:
                      </p>
                      <p className="text-sm text-gray-600 mt-1">
                        {principle.ethicalBasis}
                      </p>
                    </div>
                  </div>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Separation Guidelines */}
      <section className="py-20 bg-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-16">
            <h2 className="text-4xl font-bold text-gray-900 mb-4">
              Separation Guidelines
            </h2>
            <p className="text-xl text-gray-600 max-w-3xl mx-auto">
              Practical measures to maintain clear boundaries between editorial
              and commercial content
            </p>
            <div className="w-24 h-1 bg-green-600 mx-auto mt-6 rounded-full"></div>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
            {separationGuidelines.map((guideline, index) => (
              <div
                key={index}
                className="bg-gradient-to-br from-green-50 to-emerald-50 rounded-2xl p-6 border border-green-200 hover:shadow-lg transition-shadow duration-300"
              >
                <div className="text-center mb-6">
                  <div className="text-3xl mb-4">{guideline.icon}</div>
                  <h3 className="text-xl font-bold text-gray-900">
                    {guideline.category}
                  </h3>
                </div>

                <ul className="space-y-3">
                  {guideline.items.map((item, itemIndex) => (
                    <li
                      key={itemIndex}
                      className="flex items-start text-gray-700"
                    >
                      <div className="flex-shrink-0 w-6 h-6 bg-green-100 rounded-full flex items-center justify-center mr-3 mt-0.5">
                        <span className="text-green-600 text-sm font-bold">
                          {itemIndex + 1}
                        </span>
                      </div>
                      <span className="leading-relaxed">{item}</span>
                    </li>
                  ))}
                </ul>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Conflict Prevention */}
      <section className="py-20 bg-gray-50">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-16">
            <h2 className="text-4xl font-bold text-gray-900 mb-4">
              Conflict of Interest Prevention
            </h2>
            <p className="text-xl text-gray-600 max-w-3xl mx-auto">
              Managing high-risk scenarios that threaten reporting independence
            </p>
            <div className="w-24 h-1 bg-green-600 mx-auto mt-6 rounded-full"></div>
          </div>

          <div className="grid grid-cols-1 lg:grid-cols-3 gap-8">
            {conflictPrevention.map((scenario, index) => (
              <div
                key={index}
                className="bg-white rounded-xl p-6 hover:shadow-lg transition-shadow duration-300 border border-gray-200"
              >
                <div className="flex items-center justify-between mb-4">
                  <h3 className="text-lg font-bold text-gray-900">
                    {scenario.scenario}
                  </h3>
                  <span
                    className={`px-3 py-1 rounded-full text-xs font-semibold ${
                      scenario.riskLevel === "High"
                        ? "bg-red-100 text-red-800"
                        : "bg-yellow-100 text-yellow-800"
                    }`}
                  >
                    {scenario.riskLevel} Risk
                  </span>
                </div>

                <ul className="space-y-2">
                  {scenario.guidelines.map((guideline, guidelineIndex) => (
                    <li
                      key={guidelineIndex}
                      className="flex items-start text-sm text-gray-600"
                    >
                      <span className="text-green-500 mr-2 mt-1">•</span>
                      {guideline}
                    </li>
                  ))}
                </ul>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Compliance Framework */}
      <section className="py-20 bg-gradient-to-r from-green-600 to-green-700 text-white">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="bg-white bg-opacity-10 rounded-2xl p-8 backdrop-blur-sm">
            <div className="text-center mb-8">
              <h2 className="text-3xl font-bold mb-4">
                Compliance & Monitoring Framework
              </h2>
              <p className="text-green-100 text-lg">
                Systematic measures to ensure ongoing adherence to independence
                standards
              </p>
              <div className="w-24 h-1 bg-white mx-auto mt-4 rounded-full"></div>
            </div>

            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
              {complianceMeasures.map((measure, index) => (
                <div
                  key={index}
                  className="bg-white bg-opacity-20 rounded-xl p-4"
                >
                  <div className="flex items-center justify-between mb-2">
                    <h3 className="font-bold text-lg">{measure.measure}</h3>
                    <span className="bg-white bg-opacity-30 px-2 py-1 rounded text-sm font-semibold">
                      {measure.frequency}
                    </span>
                  </div>
                  <p className="text-green-100 text-sm">
                    {measure.description}
                  </p>
                </div>
              ))}
            </div>
          </div>
        </div>
      </section>

      {/* Violation Reporting */}
      <section className="py-20 bg-white">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-12">
            <h2 className="text-3xl font-bold text-gray-900 mb-4">
              Reporting Independence Violations
            </h2>
            <p className="text-xl text-gray-600">
              Protected channels for reporting breaches of independence
              standards
            </p>
            <div className="w-24 h-1 bg-green-600 mx-auto mt-4 rounded-full"></div>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-8">
            <div className="bg-red-50 border border-red-200 rounded-xl p-6">
              <h3 className="text-xl font-bold text-gray-900 mb-4 flex items-center">
                <span className="text-2xl text-red-600 mr-3">🚨</span>
                Immediate Reporting
              </h3>
              <ul className="space-y-3 text-gray-700">
                <li className="flex items-start">
                  <span className="text-red-500 mr-2">•</span>
                  Direct line to NAI Ethics Committee
                </li>
                <li className="flex items-start">
                  <span className="text-red-500 mr-2">•</span>
                  Anonymous reporting option available
                </li>
                <li className="flex items-start">
                  <span className="text-red-500 mr-2">•</span>
                  Whistleblower protection guaranteed
                </li>
              </ul>
            </div>

            <div className="bg-green-50 border border-green-200 rounded-xl p-6">
              <h3 className="text-xl font-bold text-gray-900 mb-4 flex items-center">
                <span className="text-2xl text-green-600 mr-3">📞</span>
                Contact Channels
              </h3>
              <ul className="space-y-3 text-gray-700">
                <li className="flex items-start">
                  <span className="text-green-500 mr-2">•</span>
                  Ethics Hotline: 72000 24451
                </li>
                <li className="flex items-start">
                  <span className="text-green-500 mr-2">•</span>
                  Email: ethics@naiindia.in
                </li>
                <li className="flex items-start">
                  <span className="text-green-500 mr-2">•</span>
                  Secure online reporting portal
                </li>
              </ul>
            </div>
          </div>
        </div>
      </section>

      {/* Call to Action */}
      <section className="py-20 bg-gray-900 text-white">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <h2 className="text-4xl font-bold mb-6">
            Uphold Independent Journalism
          </h2>
          <p className="text-xl mb-8 max-w-3xl mx-auto text-gray-300">
            Join NAI in maintaining the highest standards of editorial
            independence and protecting journalism from commercial influences
          </p>
          <div className="flex flex-col sm:flex-row justify-center space-y-4 sm:space-y-0 sm:space-x-6">
            <button className="bg-green-600 hover:bg-green-700 text-white px-8 py-4 rounded-lg font-semibold text-lg transition-colors duration-300">
              Download Independence Guidelines
            </button>
            <button className="border-2 border-white hover:bg-white hover:text-gray-900 text-white px-8 py-4 rounded-lg font-semibold text-lg transition-colors duration-300">
              Report a Concern
            </button>
          </div>
        </div>
      </section>
    </div>
  );
};

export default IndependenceOfReporting;
